/**
 * Shared streamed-token chunk assembly for the MyScribe AI socket listeners
 * (ambient listening, auto map, general chat). The worker delivers a response
 * either as ordered `{ value, orderId }` chunks or as order-less slices; this
 * assembles both into the growing message text.
 *
 * Ordered chunks are keyed by `orderId` and concatenated from index 0; chunks
 * without a numeric `orderId` are appended in arrival order so they are never
 * silently dropped. Each listener owns its own `buffers` map (keyed by chatId)
 * and passes it in, so streams from different chats stay isolated.
 */

/** A fresh per-chat buffer. */
export const createChunkBuffer = () => ({ byOrder: {}, appended: '' });

const concatOrdered = (buf) => {
  let assembled = '';
  let i = 0;
  while (buf.byOrder[i] !== undefined) {
    assembled += buf.byOrder[i];
    i += 1;
  }
  return assembled;
};

/**
 * Record a chunk into `buffers[chatId]` and return the full assembled text so
 * far. A numeric `orderId` slots the value by order; otherwise the value is
 * appended in arrival order. Passing a nullish value is a no-op write that
 * still returns the current assembled text.
 *
 * @param {Object} buffers - listener-owned map of chatId -> buffer
 * @param {string|number} chatId
 * @param {string} [value]
 * @param {number} [orderId]
 * @returns {string} assembled message text
 */
export const assembleChunks = (buffers, chatId, value, orderId) => {
  if (!buffers[chatId]) buffers[chatId] = createChunkBuffer();
  const buf = buffers[chatId];

  if (typeof orderId === 'number') {
    buf.byOrder[orderId] = value || '';
  } else if (value) {
    buf.appended += value;
  }

  return concatOrdered(buf) + buf.appended;
};

/** Drop a chat's buffer once its stream terminates. */
export const clearChunkBuffer = (buffers, chatId) => {
  if (chatId != null && buffers[chatId]) {
    delete buffers[chatId];
  }
};
